/**
 * How a shipment's carrier reads on screen.
 *
 * The courier field is a short list of parcel carriers plus 'other', and the
 * name behind 'other' lives in `courier_other`. Rendering the code alone turns
 * every named carrier into the word "Other" — which is what happened to
 * CR-7732's hauliers, whose name is stored exactly that way because a lorry
 * firm is on none of the tracking providers the codes map to.
 *
 * One helper so the grid, the detail modal, the PO-line tab and the call-off
 * tab cannot disagree, and so sorting a column by carrier sorts by what the
 * column shows. The ASN PDF (`asn_pdf_service._get_courier_display`) and the
 * create wizard's review step already did this; this is the same rule.
 */
const COURIER_LABELS: Record<string, string> = {
  fedex: 'FedEx',
  ups: 'UPS',
  dhl: 'DHL',
  tnt: 'TNT',
  dpd: 'DPD',
  other: 'Other',
};

/**
 * The carrier's display name.
 *
 * @param courier - the stored courier code
 * @param courierOther - the name behind 'other', when there is one
 * @param fallback - what to show when no carrier is recorded (default '-')
 * @returns the named carrier, the mapped label, or the fallback
 *
 * @example
 * courierLabel('other', 'SKAT TRANSPORT'); // 'SKAT TRANSPORT'
 * courierLabel('dhl');                     // 'DHL'
 */
export function courierLabel(
  courier?: string | null,
  courierOther?: string | null,
  fallback = '-',
): string {
  const named = (courierOther || '').trim();
  if (named) return named;
  const code = (courier || '').trim();
  if (!code) return fallback;
  return COURIER_LABELS[code] || code;
}
